import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import {
	BURNING_BRIDGES_PARTY_PATH,
	type BurningBridgesQualityId,
	type BurningBridgesTemplateId,
} from "./burning-bridges-preset";

/**
 * /burning-bridges-ai-video-generator 样片区的片子，做法同 rumpelstiltskin-showcase.ts。
 *
 * 竞品样片没有能用的（Starrd 带原曲、Kavel 是 TikTok 真人、LightX 跑题），全部在本页 hero 用预设出：
 * gpt-image-2 虚构人物和一只法斗当图 1，图 2 是默认的派对场景图。成片带模型生成的原创 beat，
 * 不是原曲。来源、积分和压制参数见 packages/scripts/burning-bridges-showcase/README.md。
 *
 * R2 `home/showcase/burning-bridges/` 下 clips/{id}.mp4、posters/{id}.jpg、refs/{id}-1.jpg。
 * 清单为空时样片区整个不渲染。
 */
export interface BurningBridgesShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档，拼贴 hover 时显示档位名 */
	quality: BurningBridgesQualityId;
	/** 出这条片用的模板；「Try this example」顺带切过去，狗照片才不会套人像模板 */
	template: BurningBridgesTemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 图 1、图 2 原图，「Try this example」填进 hero 的两个槽 */
	inputImages: [string, string];
}

const R2_BASE = "home/showcase/burning-bridges";

/**
 * 视频地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件；换片子时把这个数字加一
 */
const CLIP_VERSION = 3;

type Spec = Omit<
	BurningBridgesShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
>;

/**
 * 顺序即拼贴顺序：一行等高并排（VideoShowcaseGrid equalHeightRow），两条都是 HD（Seedance 2.0 Mini 720p，
 * 15 秒）。Me 带深度图参考视频出 16:9，10/12 起服务端出片后换成原曲，satin-party 同样换了音轨（v3）；宠物一图出 9:16、
 * 模型自带 beat。10/11 首版的 satin-woman / cardigan-man 是
 * 老模板出的竖版，不代表现在的 Me 效果，10/12 撤下（R2 文件还在）
 */
const SPECS: Spec[] = [
	{
		id: "satin-party",
		title: "Satin Blazer Party Host",
		description:
			"A woman in a black satin blazer dress flicks her fingers at the lens with a silver cup, tugs her collar, reaches into the crowd and ends on the where-she-at look.",
		quality: "hd",
		template: "me",
		orientation: "landscape",
		onSite: true,
		duration: 15,
		hasAudio: true,
	},
	{
		id: "frenchie",
		title: "French Bulldog Edition",
		description:
			"A French bulldog stands up with a silver shaker, reaches a paw toward the camera, pours for the crowd and raises a paw to look around.",
		quality: "hd",
		template: "pet",
		orientation: "portrait",
		onSite: true,
		duration: 15,
		hasAudio: true,
	},
];

export const BURNING_BRIDGES_PARTY_URL = buildR2AssetsImageUrl(
	BURNING_BRIDGES_PARTY_PATH,
);

export const burningBridgesShowcaseClips: BurningBridgesShowcaseClip[] =
	SPECS.map((spec) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(`${R2_BASE}/posters/${spec.id}.jpg`),
		inputImages: [
			buildR2AssetsImageUrl(`${R2_BASE}/refs/${spec.id}-1.jpg`),
			BURNING_BRIDGES_PARTY_URL,
		],
	}));

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const BURNING_BRIDGES_SHOWCASE_UPLOADED_AT = "2026-10-11T00:00:00+08:00";
